import {
    CompetitionGame,
    CompetitionTeam
} from "../services/mexfbaApi";

export interface StandingRow {
    id: string;
    nombre: string;
    grupo: string;
    position: number;
    played: number;
    wins: number;
    draws: number;
    losses: number;
    pointsFor: number;
    pointsAgainst: number;
    difference: number;
}

interface MutableStandingRow extends Omit<StandingRow, "position"> {}

type TimedCompetitionGame = CompetitionGame & {
    hora?: string;
};

function getGameHour(game: CompetitionGame): string {
    const timedGame = game as TimedCompetitionGame;

    return String(timedGame.hora || "").trim();
}

function hasConfirmedGameHour(
    game: CompetitionGame
): boolean {
    /*
        Se acepta HH:mm o HH:mm:ss.
        Ejemplos válidos: 18:00, 09:30, 18:00:00.
    */
    return /^\d{1,2}:\d{2}(?::\d{2})?$/.test(
        getGameHour(game)
    );
}

function parseGameDateTime(
    game: CompetitionGame
): Date | null {
    const date = String(game.fecha || "").trim();

    if (!date) {
        return null;
    }

    /*
        Si no hay hora confirmada, el partido se considera
        programado al mediodía local de ese día.
    */
    const hour = hasConfirmedGameHour(game)
        ? getGameHour(game)
        : "12:00:00";

    const normalizedHour =
        hour.length === 5
            ? `${hour}:00`
            : hour;

    const parsedDate = new Date(
        `${date}T${normalizedHour}`
    );

    return Number.isNaN(parsedDate.getTime())
        ? null
        : parsedDate;
}

function hasNumericScore(
    value?: string | number
): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}


/*
    Un partido sólo afecta las tablas si:

    - Tiene fecha.
    - La fecha ya ocurrió.
    - Tiene ambos marcadores numéricos.

    Un partido futuro nunca afecta la tabla aunque por error
    tenga valores en resLocal o resVisita.
*/
/*
    Un partido sólo afecta las tablas si:

    - Tiene fecha.
    - Ya llegó o pasó su fecha/hora programada.
    - Tiene ambos marcadores numéricos.

    Si no existe "hora", se usa el mediodía local
    de la fecha del partido como hora efectiva.
*/
export function isCompletedGame(
    game: CompetitionGame
): boolean {
    const gameDateTime = parseGameDateTime(game);

    if (!gameDateTime) {
        return false;
    }

    /*
        Antes de la fecha/hora efectiva jamás debe contar,
        incluso si por error ya contiene resLocal/resVisita.
    */
    if (gameDateTime.getTime() > Date.now()) {
        return false;
    }

    return (
        hasNumericScore(game.resLocal) &&
        hasNumericScore(game.resVisita)
    );
}


function createRow(team: CompetitionTeam): MutableStandingRow {
    return {
        id: team.id,
        nombre: team.nombre,
        grupo: team.grupo?.trim() || "Sin grupo",
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        pointsFor: 0,
        pointsAgainst: 0,
        difference: 0
    };
}


function createRows(
    teams: CompetitionTeam[]
): Map<string, MutableStandingRow> {
    const rows = new Map<string, MutableStandingRow>();

    teams.forEach(function (team) {
        rows.set(team.id, createRow(team));
    });

    return rows;
}

function applyGameToRows(
    rows: Map<string, MutableStandingRow>,
    game: CompetitionGame
) {
    const localRow = rows.get(game.id_local);
    const visitorRow = rows.get(game.id_visita);

    if (!localRow || !visitorRow) {
        return;
    }

    const localScore = Number(game.resLocal);
    const visitorScore = Number(game.resVisita);

    localRow.played += 1;
    visitorRow.played += 1;

    localRow.pointsFor += localScore;
    localRow.pointsAgainst += visitorScore;

    visitorRow.pointsFor += visitorScore;
    visitorRow.pointsAgainst += localScore;

    if (localScore > visitorScore) {
        localRow.wins += 1;
        visitorRow.losses += 1;
    } else if (visitorScore > localScore) {
        visitorRow.wins += 1;
        localRow.losses += 1;
    } else {
        localRow.draws += 1;
        visitorRow.draws += 1;
    }
}

function finishRows(
    rows: Map<string, MutableStandingRow>
): MutableStandingRow[] {
    return Array.from(rows.values()).map(function (row) {
        return {
            ...row,
            difference: row.pointsFor - row.pointsAgainst
        };
    });
}

function compareRecord(
    firstTeam: MutableStandingRow,
    secondTeam: MutableStandingRow
): number {
    if (secondTeam.wins !== firstTeam.wins) {
        return secondTeam.wins - firstTeam.wins;
    }

    if (firstTeam.losses !== secondTeam.losses) {
        return firstTeam.losses - secondTeam.losses;
    }

    if (secondTeam.draws !== firstTeam.draws) {
        return secondTeam.draws - firstTeam.draws;
    }

    return 0;
}

function compareSecondary(
    firstTeam: MutableStandingRow,
    secondTeam: MutableStandingRow
): number {
    if (secondTeam.difference !== firstTeam.difference) {
        return secondTeam.difference - firstTeam.difference;
    }

    if (secondTeam.pointsFor !== firstTeam.pointsFor) {
        return secondTeam.pointsFor - firstTeam.pointsFor;
    }

    return firstTeam.nombre.localeCompare(secondTeam.nombre, "es-MX");
}

function getDirectGames(
    teamIds: string[],
    games: CompetitionGame[]
): CompetitionGame[] {
    const selectedTeams = new Set(teamIds);

    return games.filter(function (game) {
        return (
            selectedTeams.has(game.id_local) &&
            selectedTeams.has(game.id_visita)
        );
    });
}

function createMiniTable(
    teamIds: string[],
    games: CompetitionGame[],
    teamsById: Map<string, CompetitionTeam>
): MutableStandingRow[] {
    const selectedTeams = teamIds
        .map(function (teamId) {
            return teamsById.get(teamId);
        })
        .filter(Boolean) as CompetitionTeam[];

    const rows = createRows(selectedTeams);

    getDirectGames(teamIds, games).forEach(function (game) {
        applyGameToRows(rows, game);
    });

    return finishRows(rows);
}

function createSameGroupTable(
    teamIds: string[],
    games: CompetitionGame[],
    teamsById: Map<string, CompetitionTeam>
): MutableStandingRow[] {
    /*
        Se crean filas para todos los equipos de la competencia.
        Esto permite contar los partidos de cada equipo contra
        cualquier rival de su mismo grupo, no sólo contra los equipos
        que están actualmente empatados.
    */
    const allRows = createRows(
        Array.from(teamsById.values())
    );

    games.forEach(function (game) {
        const localTeam = teamsById.get(game.id_local);
        const visitorTeam = teamsById.get(game.id_visita);

        if (!localTeam || !visitorTeam) {
            return;
        }

        /*
            Sólo cuentan como criterio secundario los partidos
            entre equipos que comparten grupo.
        */
        const localGroup = localTeam.grupo?.trim();
        const visitorGroup = visitorTeam.grupo?.trim();

        if (
            !localGroup ||
            !visitorGroup ||
            localGroup !== visitorGroup
        ) {
            return;
        }


        applyGameToRows(allRows, game);
    });

    /*
        Devuelve únicamente las filas de los equipos empatados,
        pero con sus estadísticas completas contra su grupo.
    */
    return finishRows(allRows).filter(function (row) {
        return teamIds.includes(row.id);
    });
}


function getRecordKey(row: MutableStandingRow): string {
    return `${row.wins}-${row.losses}-${row.draws}`;
}

function sortTiedTeams(
    teamIds: string[],
    completedGames: CompetitionGame[],
    teamsById: Map<string, CompetitionTeam>,
    depth = 0
): string[] {
    if (teamIds.length <= 1 || depth > 4) {
        return teamIds;
    }

    /*
        Primero se genera la mini-tabla sólo con los enfrentamientos
        entre los equipos empatados.
    */
    const miniTable = createMiniTable(
        teamIds,
        completedGames,
        teamsById
    );

    const miniGames = getDirectGames(teamIds, completedGames);

    /*
        Si no hay enfrentamientos directos entre los equipos empatados,
        se priorizan los partidos ante rivales del mismo grupo.
    */
    const sourceTable =
        miniGames.length > 0
            ? miniTable
            : createSameGroupTable(
                  teamIds,
                  completedGames,
                  teamsById
              );

    const rowsById = new Map(
        sourceTable.map(function (row) {
            return [row.id, row];
        })
    );

    const orderedRows = [...sourceTable].sort(function (
        firstTeam,
        secondTeam
    ) {
        const recordDifference = compareRecord(firstTeam, secondTeam);

        if (recordDifference !== 0) {
            return recordDifference;
        }

        return 0;
    });

    const groups = new Map<string, string[]>();

    orderedRows.forEach(function (row) {
        const key = getRecordKey(row);

        if (!groups.has(key)) {
            groups.set(key, []);
        }

        groups.get(key)?.push(row.id);
    });

    const result: string[] = [];

    groups.forEach(function (ids) {
        if (ids.length === 1) {
            result.push(ids[0]);
            return;
        }

        /*
            Si aún hay un empate parcial, se intenta de nuevo
            sólo con ese subconjunto.
        */
        if (ids.length < teamIds.length) {
            result.push(
                ...sortTiedTeams(
                    ids,
                    completedGames,
                    teamsById,
                    depth + 1
                )
            );

            return;
        }

        /*
            Si todos continúan empatados, se utiliza diferencia de puntos,
            puntos anotados y nombre para mantener un orden estable.
        */
        const stableRows = ids
            .map(function (id) {
                return rowsById.get(id);
            })
            .filter(Boolean) as MutableStandingRow[];

        stableRows.sort(compareSecondary);

        result.push(
            ...stableRows.map(function (row) {
                return row.id;
            })
        );
    });

    return result;
}

export function createStandingsByGroup(
    teams: CompetitionTeam[],
    games: CompetitionGame[]
): Map<string, StandingRow[]> {
    const teamsById = new Map(
        teams.map(function (team) {
            return [team.id, team];
        })
    );

    /*
        Sólo los partidos de fase de grupos y ya finalizados
        se consideran para las posiciones.
    */
    const completedGroupGames = games.filter(function (game) {
        return game.partido === "grupos" && isCompletedGame(game);
    });

    const globalRows = createRows(teams);

    completedGroupGames.forEach(function (game) {
        applyGameToRows(globalRows, game);
    });

    const rowsByGroup = new Map<string, MutableStandingRow[]>();

    finishRows(globalRows).forEach(function (row) {
        /*
            Los equipos sin grupo no aparecen en la tabla por grupos.
            Esto no modifica los datos originales.
        */
        if (!row.grupo || row.grupo === "Sin grupo") {
            return;
        }

        if (!rowsByGroup.has(row.grupo)) {
            rowsByGroup.set(row.grupo, []);
        }

        rowsByGroup.get(row.grupo)?.push(row);
    });


    const result = new Map<string, StandingRow[]>();

    rowsByGroup.forEach(function (rows, groupId) {
        const records = new Map<string, MutableStandingRow[]>();

        rows.forEach(function (row) {
            const key = getRecordKey(row);

            if (!records.has(key)) {
                records.set(key, []);
            }

            records.get(key)?.push(row);
        });

        const orderedRows: MutableStandingRow[] = [];

        Array.from(records.entries())
            .sort(function ([firstKey], [secondKey]) {
                const firstRecord = firstKey
                    .split("-")
                    .map(Number);

                const secondRecord = secondKey
                    .split("-")
                    .map(Number);

                if (secondRecord[0] !== firstRecord[0]) {
                    return secondRecord[0] - firstRecord[0];
                }

                if (firstRecord[1] !== secondRecord[1]) {
                    return firstRecord[1] - secondRecord[1];
                }

                return secondRecord[2] - firstRecord[2];
            })
            .forEach(function ([, tiedRows]) {
                if (tiedRows.length === 1) {
                    orderedRows.push(tiedRows[0]);
                    return;
                }

                const sortedIds = sortTiedTeams(
                    tiedRows.map(function (row) {
                        return row.id;
                    }),
                    completedGroupGames,
                    teamsById
                );

                sortedIds.forEach(function (teamId) {
                    const row = tiedRows.find(function (team) {
                        return team.id === teamId;
                    });

                    if (row) {
                        orderedRows.push(row);
                    }
                });
            });

        result.set(
            groupId,
            orderedRows.map(function (row, index) {
                return {
                    ...row,
                    position: index + 1
                };
            })
        );
    });

    return result;
}
